import React, { useEffect, useState, useRef, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Slider } from "../../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  Palette, Type, Square, MousePointerClick, Download, Upload, Check,
  Loader2, Copy, Trash2, Plus, Save, Monitor, Tablet, Smartphone,
  ChevronDown, Sparkles, Pencil, Eye, Wand2, Home, ShoppingBag,
  Wrench, FileText, Phone, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "../../lib/useSettings";
import { useAuth } from "../../lib/auth";

const DESIGN_KEYS = [
  "primary_color", "secondary_color", "accent_color",
  "background_color", "surface_color", "text_color", "muted_color",
  "font_display", "font_body", "font_weight_bold", "letter_spacing_tight",
  "radius_scale", "shadow_scale", "spacing_scale", "button_style",
];

const FONT_OPTIONS = [
  "Inter", "Plus Jakarta Sans", "Poppins", "Space Grotesk",
  "Playfair Display", "DM Sans", "Montserrat", "Raleway",
  "Work Sans", "Manrope", "Nunito", "Outfit",
];

const BUTTON_STYLES = [
  { value: "pill", label: "Pill", preview: "rounded-full" },
  { value: "rounded", label: "Rounded", preview: "rounded-lg" },
  { value: "sharp", label: "Sharp", preview: "rounded-none" },
];

const DEVICES = [
  { id: "desktop", label: "Desktop", icon: Monitor, width: "100%" },
  { id: "tablet", label: "Tablet", icon: Tablet, width: "768px" },
  { id: "mobile", label: "Mobile", icon: Smartphone, width: "390px" },
];

const PREVIEW_PAGES = [
  { id: "home", label: "Accueil", icon: Home, path: "/" },
  { id: "services", label: "Services", icon: Wrench, path: "/services" },
  { id: "shop", label: "Boutique", icon: ShoppingBag, path: "/shop" },
  { id: "blog", label: "Blog", icon: FileText, path: "/blog" },
  { id: "team", label: "Équipe", icon: Users, path: "/equipe" },
  { id: "contact", label: "Contact", icon: Phone, path: "/contact" },
];

function ColorSwatch({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-8 h-8 rounded-full border-2 border-[#2C2C2C] hover:border-[#444444] transition-all shrink-0 shadow-inner"
            style={{ backgroundColor: value }}
            data-testid={`color-picker-${testid}`}
            aria-label={`Modifier ${label}`}
          />
        </PopoverTrigger>
        <PopoverContent side="right" className="w-60 p-3 bg-[#1A1A1A] border-[#2C2C2C]">
          <div className="space-y-2">
            <input
              type="color"
              value={value}
              onChange={e => onChange(e.target.value)}
              className="w-full h-32 rounded-md cursor-pointer border-0 bg-transparent"
            />
            <Input
              value={value}
              onChange={e => onChange(e.target.value)}
              className="font-mono text-xs h-8 bg-[#0F0F0F] border-[#2C2C2C] text-[#F5F5F5]"
            />
          </div>
        </PopoverContent>
      </Popover>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-[#F5F5F5] truncate">{label}</div>
        <div className="text-[10px] font-mono text-[#A0A0A0] uppercase">{value}</div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#2C2C2C]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#1A1A1A] transition-colors"
      >
        <div className="flex items-center gap-2.5 text-[#F5F5F5]">
          <Icon className="w-3.5 h-3.5 text-[#A0A0A0]" />
          <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-[#A0A0A0] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

function PresetCard({ theme, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative h-20 rounded-lg border-2 overflow-hidden transition-all hover:scale-[1.02] ${
        active ? "border-[#0055FF] ring-2 ring-[#0055FF]/30" : "border-[#2C2C2C] hover:border-[#444444]"
      }`}
      style={{ background: theme.preview_gradient || `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.secondary_color} 100%)` }}
      data-testid={`preset-${theme.id}`}
    >
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
      <div className="absolute bottom-2 left-2 right-2 text-white text-left">
        <div className="text-xs font-bold truncate drop-shadow">{theme.name}</div>
      </div>
      {active && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#0055FF] flex items-center justify-center shadow-lg">
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        </div>
      )}
    </button>
  );
}

function buildThemeCss(t) {
  // CSS variable overrides injected into the iframe via postMessage
  // Convert hex to HSL approx is complex — we use CSS var overrides directly.
  return `
    :root {
      --brand-navy: ${t.primary_color} !important;
      --brand-sky: ${t.secondary_color} !important;
      --brand-red: ${t.accent_color} !important;
      --theme-bg: ${t.background_color};
      --theme-surface: ${t.surface_color};
      --theme-text: ${t.text_color};
      --theme-muted: ${t.muted_color};
      --theme-radius-scale: ${t.radius_scale};
      --theme-shadow-scale: ${t.shadow_scale};
      --theme-spacing-scale: ${t.spacing_scale};
    }
    body { font-family: "${t.font_body}", system-ui, sans-serif; }
    h1, h2, h3, h4, .font-display { font-family: "${t.font_display}", system-ui, sans-serif; letter-spacing: ${t.letter_spacing_tight}; }
    button, .rounded-full { ${t.button_style === "pill" ? "border-radius: 9999px !important;" : t.button_style === "sharp" ? "border-radius: 0 !important;" : "border-radius: 0.75rem !important;"} }
  `;
}

export default function ThemesAdmin() {
  const { refresh } = useSettings();
  const { user } = useAuth();
  const isAgency = user?.role === "admin";
  const [themes, setThemes] = useState([]);
  const [activeThemeId, setActiveThemeId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null); // local draft theme for live preview
  const [device, setDevice] = useState("desktop");
  const [previewPage, setPreviewPage] = useState("home");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const iframeRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/themes");
      setThemes(data.items || []);
      setActiveThemeId(data.active_theme_id);
      if (!selectedId && data.items.length) {
        const first = data.items.find(t => t.id === data.active_theme_id) || data.items[0];
        setSelectedId(first.id);
        setEditing({ ...first });
      }
    } catch {
      toast.error("Impossible de charger les thèmes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // When user switches preset — load it as draft
  useEffect(() => {
    if (!selectedId || !themes.length) return;
    const found = themes.find(t => t.id === selectedId);
    if (found) setEditing({ ...found });
  }, [selectedId]); // eslint-disable-line

  // Inject CSS into the iframe whenever `editing` OR `previewPage` changes (live preview)
  useEffect(() => {
    if (!editing || !iframeRef.current) return;
    const css = buildThemeCss(editing);
    const inject = () => {
      try {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        let style = doc.getElementById("__theme_preview_override__");
        if (!style) {
          style = doc.createElement("style");
          style.id = "__theme_preview_override__";
          doc.head.appendChild(style);
        }
        style.textContent = css;
      } catch (e) { /* same-origin required — ok since both hosted on same domain */ }
    };
    inject();
    const t1 = setTimeout(inject, 400); // re-try after iframe first paint
    const t2 = setTimeout(inject, 1200); // second retry for slower SPA nav
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [editing, previewPage]);

  const current = themes.find(t => t.id === selectedId);
  const isPreset = current?.is_preset;
  const isDirty = useMemo(() => {
    if (!current || !editing) return false;
    return DESIGN_KEYS.some(k => current[k] !== editing[k]);
  }, [current, editing]);

  const setField = (key, value) => {
    setEditing(prev => ({ ...prev, [key]: value }));
  };

  const saveTheme = async (opts = {}) => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = { ...editing };
      // Strip immutable keys from body
      ["id", "is_preset", "created_at", "updated_at"].forEach(k => delete payload[k]);
      if (isPreset) {
        // Preset → create a copy instead
        const { data } = await api.post("/themes", { ...payload, name: opts.forkedName || `${editing.name} (perso)` });
        toast.success("Thème dupliqué — vous éditez maintenant la copie");
        await load();
        setSelectedId(data.id);
      } else {
        await api.put(`/themes/${editing.id}`, payload);
        toast.success("Thème enregistré");
        await load();
      }
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const applyTheme = async () => {
    if (!current) return;
    setApplying(true);
    try {
      await api.post(`/themes/${current.id}/apply`, {});
      await refresh();
      toast.success(`"${current.name}" appliqué sur le site public`);
      setActiveThemeId(current.id);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setApplying(false); }
  };

  const duplicateTheme = async () => {
    if (!current) return;
    try {
      const { data } = await api.post(`/themes/${current.id}/duplicate`, {});
      await load();
      setSelectedId(data.id);
      toast.success(`Copie créée : "${data.name}"`);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const deleteTheme = async () => {
    if (!current || isPreset) return;
    if (!window.confirm(`Supprimer le thème "${current.name}" ?`)) return;
    try {
      await api.delete(`/themes/${current.id}`);
      toast.success("Thème supprimé");
      setSelectedId(null);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const createNew = async () => {
    try {
      const base = editing || themes[0] || {};
      const payload = {};
      DESIGN_KEYS.forEach(k => { if (base[k] !== undefined) payload[k] = base[k]; });
      payload.name = `Nouveau thème ${new Date().toLocaleDateString("fr-FR")}`;
      payload.description = "";
      const { data } = await api.post("/themes", payload);
      await load();
      setSelectedId(data.id);
      toast.success("Thème créé");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const exportJson = () => {
    if (!editing) return;
    const blob = new Blob([JSON.stringify(editing, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `theme-${(editing.name || "custom").replace(/\s+/g, "-").toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Thème exporté");
  };

  const importJson = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const imported = JSON.parse(ev.target.result);
        const payload = {};
        DESIGN_KEYS.forEach(k => { if (imported[k] !== undefined) payload[k] = imported[k]; });
        payload.name = imported.name || "Thème importé";
        payload.description = imported.description || "";
        const { data } = await api.post("/themes", payload);
        await load();
        setSelectedId(data.id);
        toast.success(`"${data.name}" importé`);
      } catch (err) {
        toast.error("JSON invalide : " + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const doRename = async () => {
    if (!renameValue.trim() || isPreset) { setRenameOpen(false); return; }
    try {
      const payload = { ...editing, name: renameValue.trim() };
      ["id", "is_preset", "created_at", "updated_at"].forEach(k => delete payload[k]);
      await api.put(`/themes/${editing.id}`, payload);
      await load();
      toast.success("Renommé");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setRenameOpen(false); }
  };

  const generateWithAI = async () => {
    const p = (aiPrompt || "").trim();
    if (!p) { toast.error("Décrivez le thème souhaité"); return; }
    setAiGenerating(true);
    try {
      const { data } = await api.post("/themes/ai-generate", {
        prompt: p,
        base_theme_id: editing?.id || null,
      });
      await load();
      setSelectedId(data.id);
      setEditing({ ...data });
      setAiOpen(false);
      setAiPrompt("");
      toast.success(`Thème généré : "${data.name}"`);
    } catch (e) {
      toast.error("Génération IA : " + (e?.response?.data?.detail || e.message));
    } finally { setAiGenerating(false); }
  };

  const deviceObj = DEVICES.find(d => d.id === device) || DEVICES[0];

  if (loading && !editing) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center bg-[#0F0F0F] text-[#A0A0A0]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement des thèmes…
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] w-full overflow-hidden flex bg-[#0F0F0F] text-[#F5F5F5]" data-testid="theme-builder">
      {/* LEFT SIDEBAR — Controls */}
      <aside className="w-80 h-full border-r border-[#2C2C2C] flex flex-col bg-[#0F0F0F] shrink-0" data-testid="theme-sidebar">
        {/* Header */}
        <div className="h-14 border-b border-[#2C2C2C] flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#0055FF]" />
            <span className="text-sm font-semibold tracking-tight">Theme Studio</span>
            {isAgency ? (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-[#0055FF]/15 text-[#60A5FA] border border-[#0055FF]/30 px-1.5 py-0.5 rounded-full" data-testid="role-badge-agency">
                Agence
              </span>
            ) : (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-[#34C759]/15 text-[#4ADE80] border border-[#34C759]/30 px-1.5 py-0.5 rounded-full" data-testid="role-badge-client">
                Client
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {isAgency && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setAiOpen(true)}
                className="h-8 px-2 text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#1A1A1A] gap-1"
                title="Générer un thème avec l'IA"
                data-testid="theme-ai-generate-btn"
              >
                <Wand2 className="w-3.5 h-3.5 text-[#60A5FA]" />
                <span className="text-[11px] font-semibold">IA</span>
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={createNew}
              className="h-8 w-8 p-0 text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#1A1A1A]"
              title="Créer un nouveau thème"
              data-testid="theme-create-new"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Presets grid */}
        <div className="border-b border-[#2C2C2C]">
          <div className="px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A0A0A0] mb-2.5">
              Thèmes ({themes.length})
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="theme-presets-grid">
              {themes.map(t => (
                <PresetCard
                  key={t.id}
                  theme={t}
                  active={selectedId === t.id}
                  onClick={() => setSelectedId(t.id)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Design controls */}
        {editing && (
          <div className="flex-1 overflow-y-auto">
            <Section icon={Palette} title="Couleurs">
              <ColorSwatch label="Primaire (navy)" value={editing.primary_color} onChange={v => setField("primary_color", v)} testid="primary" />
              <ColorSwatch label="Secondaire (sky)" value={editing.secondary_color} onChange={v => setField("secondary_color", v)} testid="secondary" />
              <ColorSwatch label="Accent (red)" value={editing.accent_color} onChange={v => setField("accent_color", v)} testid="accent" />
              <ColorSwatch label="Arrière-plan" value={editing.background_color} onChange={v => setField("background_color", v)} testid="background" />
              <ColorSwatch label="Surface" value={editing.surface_color} onChange={v => setField("surface_color", v)} testid="surface" />
              <ColorSwatch label="Texte" value={editing.text_color} onChange={v => setField("text_color", v)} testid="text" />
            </Section>

            <Section icon={Type} title="Typographie">
              <div>
                <Label className="text-[10px] font-medium text-[#A0A0A0] uppercase tracking-wider">Titres (display)</Label>
                <Select value={editing.font_display} onValueChange={v => setField("font_display", v)}>
                  <SelectTrigger className="h-9 mt-1 bg-[#1A1A1A] border-[#2C2C2C] text-[#F5F5F5]" data-testid="typography-display-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2C2C2C] text-[#F5F5F5]">
                    {FONT_OPTIONS.map(f => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-medium text-[#A0A0A0] uppercase tracking-wider">Corps (body)</Label>
                <Select value={editing.font_body} onValueChange={v => setField("font_body", v)}>
                  <SelectTrigger className="h-9 mt-1 bg-[#1A1A1A] border-[#2C2C2C] text-[#F5F5F5]" data-testid="typography-body-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2C2C2C] text-[#F5F5F5]">
                    {FONT_OPTIONS.map(f => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </Section>

            <Section icon={Square} title="Formes">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label className="text-[10px] font-medium text-[#A0A0A0] uppercase tracking-wider">Rondeurs</Label>
                  <span className="text-[10px] font-mono text-[#A0A0A0]">{editing.radius_scale.toFixed(2)}</span>
                </div>
                <Slider min={0.2} max={1.5} step={0.05} value={[editing.radius_scale]} onValueChange={([v]) => setField("radius_scale", v)} data-testid="radius-slider" />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label className="text-[10px] font-medium text-[#A0A0A0] uppercase tracking-wider">Ombres</Label>
                  <span className="text-[10px] font-mono text-[#A0A0A0]">{editing.shadow_scale.toFixed(2)}</span>
                </div>
                <Slider min={0} max={1.5} step={0.05} value={[editing.shadow_scale]} onValueChange={([v]) => setField("shadow_scale", v)} data-testid="shadow-slider" />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label className="text-[10px] font-medium text-[#A0A0A0] uppercase tracking-wider">Espacement</Label>
                  <span className="text-[10px] font-mono text-[#A0A0A0]">{editing.spacing_scale.toFixed(2)}</span>
                </div>
                <Slider min={0.8} max={1.3} step={0.05} value={[editing.spacing_scale]} onValueChange={([v]) => setField("spacing_scale", v)} data-testid="spacing-slider" />
              </div>
            </Section>

            <Section icon={MousePointerClick} title="Boutons">
              <div className="grid grid-cols-3 gap-1.5">
                {BUTTON_STYLES.map(b => (
                  <button
                    key={b.value}
                    type="button"
                    onClick={() => setField("button_style", b.value)}
                    className={`h-11 text-[10px] uppercase tracking-wider font-semibold border transition-all ${b.preview} ${
                      editing.button_style === b.value
                        ? "border-[#0055FF] bg-[#0055FF]/20 text-[#F5F5F5]"
                        : "border-[#2C2C2C] hover:border-[#444444] text-[#A0A0A0]"
                    }`}
                    data-testid={`button-style-${b.value}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </Section>
          </div>
        )}

        {/* Footer actions */}
        {editing && (
          <div className="border-t border-[#2C2C2C] p-3 space-y-2 shrink-0">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={duplicateTheme} className="flex-1 h-8 bg-transparent border-[#2C2C2C] text-[#F5F5F5] hover:bg-[#1A1A1A] hover:border-[#444444]" data-testid="duplicate-btn">
                <Copy className="w-3 h-3 mr-1.5" />Dupliquer
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setRenameValue(editing.name); setRenameOpen(true); }} disabled={isPreset} className="flex-1 h-8 bg-transparent border-[#2C2C2C] text-[#F5F5F5] hover:bg-[#1A1A1A] hover:border-[#444444] disabled:opacity-40" data-testid="rename-btn">
                <Pencil className="w-3 h-3 mr-1.5" />Renommer
              </Button>
            </div>
            <Button
              size="sm"
              onClick={() => saveTheme()}
              disabled={saving || !isDirty}
              className="w-full h-9 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold disabled:opacity-40"
              data-testid="save-theme-button"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {isPreset ? "Forker en thème perso" : "Enregistrer les modifications"}
            </Button>
            {!isPreset && (
              <Button size="sm" variant="ghost" onClick={deleteTheme} className="w-full h-8 text-[#FF3B30] hover:bg-[#FF3B30]/10" data-testid="delete-theme-btn">
                <Trash2 className="w-3 h-3 mr-1.5" />Supprimer
              </Button>
            )}
          </div>
        )}
      </aside>

      {/* RIGHT CANVAS — Live preview */}
      <div className="flex-1 h-full flex flex-col relative bg-neutral-900">
        {/* Top toolbar */}
        <div className="h-14 border-b border-[#2C2C2C] flex items-center justify-between px-4 bg-[#0F0F0F] shrink-0" data-testid="theme-builder-header">
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#A0A0A0] uppercase tracking-wider">Thème sélectionné</span>
            <span className="font-semibold text-sm text-[#F5F5F5]">{editing?.name || "—"}</span>
            {activeThemeId === selectedId && (
              <span className="text-[10px] uppercase tracking-wider font-bold bg-[#34C759]/15 text-[#4ADE80] border border-[#34C759]/30 px-2 py-0.5 rounded-full" data-testid="badge-en-production">
                ● En production
              </span>
            )}
            {isDirty && (
              <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full">
                Modifié
              </span>
            )}
          </div>

          {/* Device switcher */}
          <div className="flex items-center gap-1 p-1 rounded-lg bg-[#1A1A1A] border border-[#2C2C2C]" data-testid="device-switcher">
            {DEVICES.map(d => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDevice(d.id)}
                className={`h-7 w-9 flex items-center justify-center rounded transition-colors ${
                  device === d.id ? "bg-[#0055FF] text-white" : "text-[#A0A0A0] hover:text-[#F5F5F5]"
                }`}
                title={d.label}
                data-testid={`device-${d.id}`}
              >
                <d.icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {isAgency && (
              <>
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="h-8 bg-transparent border-[#2C2C2C] text-[#F5F5F5] hover:bg-[#1A1A1A]" data-testid="import-json-btn">
                  <Upload className="w-3 h-3 mr-1.5" />Import
                </Button>
                <input ref={fileInputRef} type="file" accept=".json,application/json" onChange={importJson} className="hidden" />
                <Button size="sm" variant="outline" onClick={exportJson} disabled={!editing} className="h-8 bg-transparent border-[#2C2C2C] text-[#F5F5F5] hover:bg-[#1A1A1A]" data-testid="export-json-btn">
                  <Download className="w-3 h-3 mr-1.5" />Export
                </Button>
              </>
            )}
            <Button
              size="sm"
              onClick={applyTheme}
              disabled={applying || !current || activeThemeId === current.id}
              className="h-8 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold px-4"
              data-testid="apply-theme-button"
            >
              {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              {activeThemeId === current?.id ? "En production" : "Appliquer sur le site"}
            </Button>
          </div>
        </div>

        {/* Live iframe */}
        <div className="flex-1 p-6 overflow-auto flex items-start justify-center" data-testid="iframe-preview-container">
          <div
            className="bg-white shadow-2xl rounded-lg overflow-hidden border border-[#2C2C2C] transition-all duration-300"
            style={{ width: deviceObj.width, maxWidth: "100%", height: "calc(100vh - 10rem)" }}
          >
            <iframe
              ref={iframeRef}
              src={(PREVIEW_PAGES.find(p => p.id === previewPage) || PREVIEW_PAGES[0]).path}
              title="Live preview"
              className="w-full h-full border-0 bg-white"
              data-testid="theme-preview-iframe"
            />
          </div>
        </div>

        {/* Page selector — floating strip at bottom */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 p-1.5 rounded-full bg-[#1A1A1A]/95 backdrop-blur border border-[#2C2C2C] shadow-2xl z-10" data-testid="preview-page-selector">
          {PREVIEW_PAGES.map(p => {
            const active = previewPage === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPreviewPage(p.id)}
                className={`h-8 flex items-center gap-1.5 px-3 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                  active ? "bg-[#0055FF] text-white" : "text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#2C2C2C]"
                }`}
                data-testid={`preview-page-${p.id}`}
                title={`Preview ${p.label}`}
              >
                <p.icon className="w-3 h-3" />
                <span>{p.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Rename modal */}
      {renameOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setRenameOpen(false)}>
          <div className="bg-[#1A1A1A] border border-[#2C2C2C] rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-[#F5F5F5] mb-3">Renommer le thème</h3>
            <Input
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              autoFocus
              className="bg-[#0F0F0F] border-[#2C2C2C] text-[#F5F5F5]"
              onKeyDown={e => { if (e.key === "Enter") doRename(); }}
              data-testid="rename-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => setRenameOpen(false)} className="bg-transparent border-[#2C2C2C] text-[#F5F5F5]">Annuler</Button>
              <Button size="sm" onClick={doRename} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="rename-confirm">Valider</Button>
            </div>
          </div>
        </div>
      )}

      {/* AI Generator modal (agency only) */}
      {aiOpen && isAgency && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setAiOpen(false)} data-testid="ai-theme-modal">
          <div className="bg-[#1A1A1A] border border-[#2C2C2C] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#60A5FA] to-[#0055FF] flex items-center justify-center shadow-lg">
                  <Wand2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#F5F5F5]">Générer un thème avec l'IA</h3>
                  <p className="text-[11px] text-[#A0A0A0]">Claude Sonnet · direction artistique</p>
                </div>
              </div>
              <button type="button" onClick={() => setAiOpen(false)} className="text-[#A0A0A0] hover:text-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>

            <Label className="text-[10px] font-medium text-[#A0A0A0] uppercase tracking-wider">Brief créatif</Label>
            <textarea
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              placeholder="Ex : cabinet d'avocats, ambiance haut de gamme, bleu nuit et or, typographie éditoriale, peu de rondeurs, ombres subtiles."
              rows={5}
              className="w-full mt-1.5 rounded-lg bg-[#0F0F0F] border border-[#2C2C2C] text-[#F5F5F5] text-sm p-3 resize-none focus:border-[#0055FF] focus:outline-none"
              autoFocus
              data-testid="ai-prompt-input"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[
                "luxe or & noir pour horloger",
                "clinique dentaire apaisant vert menthe",
                "cabinet d'avocats bleu nuit & ivoire",
                "boulangerie artisanale terracotta",
                "startup tech néon cyan & violet",
              ].map(ex => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => setAiPrompt(ex)}
                  className="text-[10px] px-2 py-1 rounded-full border border-[#2C2C2C] text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#444444] transition-colors"
                  data-testid={`ai-prompt-suggest-${ex.slice(0, 12)}`}
                >
                  {ex}
                </button>
              ))}
            </div>

            {editing && (
              <p className="text-[11px] text-[#A0A0A0] mt-3">
                Point de départ : <span className="text-[#F5F5F5] font-medium">{editing.name}</span> (l'IA s'en inspirera)
              </p>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <Button size="sm" variant="outline" onClick={() => setAiOpen(false)} disabled={aiGenerating} className="bg-transparent border-[#2C2C2C] text-[#F5F5F5]">Annuler</Button>
              <Button
                size="sm"
                onClick={generateWithAI}
                disabled={aiGenerating || !aiPrompt.trim()}
                className="bg-gradient-to-br from-[#60A5FA] to-[#0055FF] hover:opacity-90 text-white font-semibold"
                data-testid="ai-theme-generate-confirm"
              >
                {aiGenerating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
                {aiGenerating ? "Génération…" : "Générer"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
